/**
 * API client for communicating with the Rotagen service.
 * This module provides functions for triggering rota generation
 * and retrieving generation results.
 */

import type {
  RotaTemplate,
  RotaProposal,
  Identifiable,
  CommitResponse,
  CommitFailureResult,
  Forecast,
} from '@repo/contracts';
import type { ErrorCategory, ErrorResponse, ErrorResponseBody } from '@repo/errors';
import { buildRequestHeaders } from '@/lib/auth/build-request-headers';
import { getConfig } from '@/lib/config/config-registry';
import type { ApiValidationIssue } from '@/lib/error-utils';

/**
 * Request payload for generating a rota from a template.
 *
 * Contains all the information needed to generate a rota for a specific
 * site and date range using a template's shift definitions and rules.
 */
export interface GenerateRotaRequest {
  /** The site for which the rota is being generated */
  site: Identifiable;
  /** The rota template containing shift definitions, staffing requirements, and rules */
  template: RotaTemplate;
  /**
   * ISO 8601 date range string defining the rota period (half-open interval).
   * Format: "YYYY-MM-DD/YYYY-MM-DD" (e.g., "2025-01-01/2025-01-31")
   * The end date is exclusive, so "2025-01-01/2025-01-08" generates days 1-7 January.
   */
  dateRange: string;
  /**
   * IANA time-zone identifier (e.g. `'Europe/London'`, `'Australia/Sydney'`)
   * used to interpret the `dateRange` boundaries (A11 / D42). When omitted,
   * the backend falls back to `'Europe/London'`.
   *
   * **Forwards-compat caveat.** Phase 0 finding 0.7b confirmed today's
   * `GenerateRotaRequestSchema` (apps/rotagen) silently strips unknown
   * keys via Zod default, so this field is **accepted but ignored** at
   * the backend. The FE-side wiring is in place so A11 ships with v1 —
   * the backend widens the schema in a coordinated follow-on PR (out of
   * scope for this rebuild per non-goals).
   */
  timeZone?: string;
  /**
   * Optional per-day demand figures for the generation period (Phase 4 /
   * D56). Keyed by ISO date (`YYYY-MM-DD`) to a `Capacity` ({@link Forecast}).
   * rotagen pairs each day's forecast with the template `baseline` to scale
   * headcount on scaling-enabled shifts (`linear`/`ratio`); omitted ⇒ shifts
   * sit at their base count. Only sent for templates with demand scaling on.
   */
  forecast?: Forecast;
  /** Optional configuration parameters for customising the generation process */
  options?: unknown;
}

/**
 * Response from POST /v1/generations.
 *
 * Creates a new RotaGeneration containing a RotaProposal.
 * The `id` is the generationId used for subsequent GET and commit calls.
 */
export interface CreateGenerationResponse {
  /** The generation ID used for GET and commit calls */
  id: string;
  /** The generated rota proposal */
  proposal: RotaProposal;
}

/**
 * A single validation issue from the API response. Alias of the canonical
 * frontend issue shape ({@link ApiValidationIssue}); kept as a named export
 * because consumers (e.g. the apply-to-rota modal) reference it.
 */
export type RotagenValidationIssue = ApiValidationIssue;

/**
 * Error response structure from the Rotagen API — the standard `@repo/errors`
 * envelope, optionally carrying validation issues.
 */
export type RotagenApiErrorResponse = ErrorResponseBody<
  ErrorResponse & { issues?: RotagenValidationIssue[] }
>;

/**
 * Custom error class for Rotagen API errors.
 *
 * Provides structured access to error details including validation issues
 * when the API returns a 400 status code.
 */
export class RotagenApiError extends Error {
  /** HTTP status code from the response */
  readonly statusCode: number;
  /** Error category from the API (e.g., 'validation', 'system') */
  readonly category: ErrorCategory | 'unknown';
  /** Error code from the API (e.g., 'ZOD_VALIDATION_ERROR') */
  readonly code: string;
  /** Validation issues if this is a validation error */
  readonly issues: RotagenValidationIssue[];

  constructor(params: {
    message: string;
    statusCode: number;
    category: ErrorCategory | 'unknown';
    code: string;
    issues?: RotagenValidationIssue[];
  }) {
    super(params.message);
    this.name = 'RotagenApiError';
    this.statusCode = params.statusCode;
    this.category = params.category;
    this.code = params.code;
    this.issues = params.issues ?? [];
  }

  /**
   * Returns true if this is a validation error (HTTP 400).
   */
  isValidationError(): boolean {
    return this.statusCode === 400 && this.category === 'validation';
  }

  /**
   * Returns a user-friendly error message.
   *
   * For validation errors, includes details about which fields have issues.
   * For other errors, returns a generic message appropriate for display to users.
   */
  getUserFriendlyMessage(): string {
    if (this.isValidationError() && this.issues.length > 0) {
      const issueMessages = this.issues.map((issue) => `${issue.field}: ${issue.message}`);
      return `The request contains invalid data:\n${issueMessages.join('\n')}`;
    }

    if (this.isValidationError()) {
      return 'The request contains invalid data. Please check your input and try again.';
    }

    if (this.statusCode === 401) {
      return 'You are not authorised to perform this action. Please log in and try again.';
    }

    if (this.statusCode === 403) {
      return 'You do not have permission to perform this action.';
    }

    if (this.statusCode === 404) {
      return 'The requested resource was not found.';
    }

    if (this.statusCode >= 500) {
      return 'An unexpected error occurred. Please try again later.';
    }

    return this.message;
  }
}

/**
 * Performs a request against the rotagen service, resolving the base URL + auth
 * from the host config (via the registry). On a 401 it refreshes the token once
 * (`auth.refreshAccessToken`) and retries a single time with fresh headers — no
 * loop. Callers check `response.ok` and map errors via {@link parseErrorResponse}.
 */
async function rotagenFetch(path: string, init: RequestInit): Promise<Response> {
  const config = getConfig();
  const url = `${config.rotagenApiUrl}${path}`;
  const send = async (): Promise<Response> =>
    fetch(url, { ...init, headers: { ...(await buildRequestHeaders()), ...init.headers } });

  let response = await send();
  if (response.status === 401) {
    await config.auth.refreshAccessToken();
    response = await send();
  }
  return response;
}

/**
 * Parses an error response from the Rotagen API.
 *
 * @param response - The fetch Response object
 * @returns A RotagenApiError with structured error information
 */
async function parseErrorResponse(response: Response): Promise<RotagenApiError> {
  let errorData: RotagenApiErrorResponse | null = null;

  try {
    errorData = (await response.json()) as RotagenApiErrorResponse;
  } catch {
    // Response body is not valid JSON
  }

  if (errorData?.error) {
    return new RotagenApiError({
      message: errorData.error.message,
      statusCode: response.status,
      category: errorData.error.category,
      code: errorData.error.code,
      issues: errorData.error.issues,
    });
  }

  // Fallback for non-standard error responses
  return new RotagenApiError({
    message: `Request failed with status ${response.status}`,
    statusCode: response.status,
    category: 'unknown',
    code: 'UNKNOWN_ERROR',
  });
}

/**
 * Creates a new rota generation from a template.
 *
 * Makes a POST request to the two-step generation endpoint. This is the first
 * step in the propose/commit flow — it creates a RotaGeneration containing a
 * RotaProposal. The returned `id` is used as the generationId for subsequent
 * GET and commit calls.
 *
 * @param request - The rota generation request containing site, template, and date range
 * @returns A CreateGenerationResponse containing the generationId and proposal
 * @throws {RotagenApiError} If the API returns an error response
 */
export async function createGeneration(
  request: GenerateRotaRequest,
): Promise<CreateGenerationResponse> {
  const response = await rotagenFetch('/v1/generations', {
    method: 'POST',
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    throw await parseErrorResponse(response);
  }

  return response.json() as Promise<CreateGenerationResponse>;
}

/** Options forwarded to the commit endpoint (D57). */
export interface CommitGenerationOptions {
  /**
   * Acknowledges that committing this rota will overwrite any existing
   * draft shifts in the target period. When `false` or omitted, rotagen
   * rejects the commit with 422 if drafts are present. The wizard's
   * `RotaProposalPreview` exposes this as an opt-in checkbox.
   */
  replaceExistingRota?: boolean;
  /**
   * Commits despite ignorable Rotaready validation issues (warnings). Set it
   * only after the user has seen the warnings from a `COMMIT_WARNINGS`
   * failure. Rotaready then skips every validation check for the commit.
   */
  ignoreWarnings?: boolean;
}

/**
 * Commits a previously proposed rota generation.
 *
 * Makes a POST request to commit the proposal associated with the given
 * generationId. This is the second step in the propose/commit flow.
 *
 * Pass `options.replaceExistingRota: true` (D57) to acknowledge that
 * existing draft shifts in the target period will be overwritten —
 * without it, rotagen 422s when drafts are present. Off by default;
 * the wizard's `RotaProposalPreview` toggles this via a consent
 * checkbox.
 *
 * @param generationId - The ID of the generation to commit
 * @param options - Optional commit-time flags (see {@link CommitGenerationOptions})
 * @returns A CommitResponse indicating success or failure with details
 * @throws {RotagenApiError} If the API returns an error response
 */
export async function commitGeneration(
  generationId: string,
  options: CommitGenerationOptions = {},
): Promise<CommitResponse> {
  const response = await rotagenFetch(`/v1/generations/${generationId}/commit`, {
    method: 'POST',
    body: JSON.stringify(options),
  });

  // Handle error responses, ignoring 422 as it is handled as a CommitResponse below
  if (!response.ok && response.status !== 422) {
    throw await parseErrorResponse(response);
  }

  // Handle an unsuccessful result. The outcome is in `result.success`; turning
  // it into a thrown `RotagenApiError` carries the specific issues so the UI can
  // show what's wrong and tell warnings from errors.
  const body = (await response.json()) as CommitResponse;
  if (!body.result.success) {
    throw errorFromCommitFailure(body.result, response.status);
  }

  return body;
}

/**
 * Builds a `RotagenApiError` from a failed `CommitResult`, flattening its
 * validation detail into `issues` so `getUserFriendlyMessage` / the commit
 * preview can surface the specific failures.
 */
function errorFromCommitFailure(result: CommitFailureResult, statusCode: number): RotagenApiError {
  return new RotagenApiError({
    message: result.errorMessage,
    statusCode,
    category: 'validation',
    code: result.errorCode,
    issues: collectCommitIssues(result),
  });
}

/**
 * Flattens a failed commit's validation detail into the frontend issue shape.
 * `validationIssues` is the canonical list: it uses `path` as the field and
 * keeps each issue's `severity` and `context`. The issues nested in each
 * structured `errors[]` entry describe the same failures without that detail,
 * so they are only read when there are no `validationIssues`.
 */
function collectCommitIssues(result: CommitFailureResult): RotagenValidationIssue[] {
  if (result.validationIssues?.length) {
    return result.validationIssues.map((issue) => ({
      field: issue.path ?? '',
      message: issue.message,
      code: issue.code,
      severity: issue.severity,
      context: issue.context,
    }));
  }

  return (result.errors ?? []).flatMap((entry) => {
    const nested = (entry as ErrorResponse & { issues?: RotagenValidationIssue[] }).issues ?? [];
    return nested.map((issue) => ({
      field: issue.field,
      message: issue.message,
      code: issue.code,
    }));
  });
}
